import { useEffect, useCallback, useState, memo, useMemo } from "react";

import {
  Button,
  LoadingSkeleton,
  IconButton,
  IconName,
} from "@narmi/design_system";
import { Drawer } from "cerulean";
import { entities } from "byzantine";

import cc from "classcat";
import QuickActionLink from "../QuickActionLink";
import useDragHorizontalScroll from "../../hooks/useDragHorizontalScroll";
import QuickActionsEditor from "../QuickActionsEditor";
import { useQuickActionLabel } from "../useQuickActionLabel";
import styles from "./QuickActionsBar.module.scss";

const QuickActionButton = memo(
  ({ action, index }: { action: API.QuickActionItem; index: number }) => {
    const label = useQuickActionLabel(action);

    return (
      <QuickActionLink
        key={action.key}
        to={action.link}
        className={styles.actionItem}
        draggable={false}
        tabIndex={-1}
      >
        <Button
          label={label}
          size="s"
          startIcon={action.icon_name as IconName}
          kind={index === 0 ? "primary" : "tonal"}
        />
      </QuickActionLink>
    );
  }
);

const QuickActionsBar = () => {
  const quickActions = entities.quickActions.useQuickActions();
  const {
    send: fetchQuickActions,
    loading,
    error,
  } = entities.quickActions.useFetchQuickActions();

  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const {
    ref: viewportRef,
    isDragging,
    isScrollable,
    recalculate,
    handlers,
  } = useDragHorizontalScroll<HTMLDivElement>({
    dragThresholdPx: 6,
    translateVerticalWheel: true,
    enableOnTouch: false, // keep native touch scroll
  });

  useEffect(() => {
    fetchQuickActions();
  }, [fetchQuickActions]);

  // Recalculate scrollability when data finishes loading or item count changes.
  // The rAF re-measure handles late layout shifts from icon fonts or async CSS.
  useEffect(() => {
    if (!loading) {
      recalculate();
      const id = requestAnimationFrame(() => {
        recalculate();
      });
      return () => {
        cancelAnimationFrame(id);
      };
    }
    return undefined;
  }, [loading, quickActions.length, recalculate]);

  const handleOpenDrawer = useCallback(() => {
    setIsEditorOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsEditorOpen(false);
  }, []);

  const viewportClassName = cc([
    styles.viewport,
    isDragging && styles.dragging,
  ]);

  const quickActionButtons = useMemo(
    () =>
      quickActions.map(
        ({ action }: API.QuickActionWithPosition, index: number) => (
          <QuickActionButton key={action.key} action={action} index={index} />
        )
      ),
    [quickActions]
  );

  return (
    <div className={styles.quickActionsBar}>
      <LoadingSkeleton isLoading={loading} lines={1} />

      {!loading && !error && (
        <div className={styles.row}>
          <div
            className={viewportClassName}
            ref={viewportRef}
            role="navigation"
            aria-label="Quick actions"
            {...handlers}
          >
            <div className={styles.actionsTrack}>
              {quickActionButtons}

              {/* Keep menu button inside the track so it follows content when not scrollable */}
              <div className={styles.menuButton}>
                <IconButton
                  kind="plain"
                  label="More actions"
                  name="more-vertical"
                  testId="quick-actions-menu-button"
                  onClick={handleOpenDrawer}
                />
              </div>
            </div>
          </div>

          <div className={styles.overlayContainer}>
            {isScrollable && (
              // Visual hint that additional items are off-screen to the right
              <div className={styles.gradientOverlay} aria-hidden="true" />
            )}
          </div>
        </div>
      )}

      <Drawer
        isOpen={isEditorOpen}
        onUserDismiss={handleCloseDrawer}
        depth="530px"
        paddingSize="l"
        showControls={false}
      >
        <QuickActionsEditor />
      </Drawer>
    </div>
  );
};

export default memo(QuickActionsBar);
